import {
  Alert,
  AlertDescription,
  AlertTitle,
  BarList,
  Button,
  DataTable,
  EmptyState,
  formatCentsBRL,
  formatCount,
  formatDate,
  formatDayMonth,
  formatNumber,
  MetricCard,
  PageLayout,
  relativeChange,
  SectionCard,
  Sparkline,
  TimeSeriesChart,
  TrendBadge,
} from '@repo/ui'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  AlertTriangleIcon,
  BanknoteIcon,
  ClipboardListIcon,
  GaugeIcon,
  LineChartIcon,
  ShieldAlertIcon,
  UsersIcon,
} from 'lucide-react'
import type { ReactNode } from 'react'

import { AttentionBadges } from '@/components/attention-badges'
import { ErrorState } from '@/components/error-state'
import { OrgCell } from '@/components/org-cell'
import { useAdminOverview } from '@/hooks/use-admin-overview'
import { useSyncRevenue } from '@/hooks/use-revenue'
import type { AdminAuditEvent, AdminOverview } from '@/lib/api-types'
import { attentionSignals } from '@/lib/attention'
import {
  MRR_SOURCE_HINTS,
  MRR_SOURCE_LABELS,
  translateAuditType,
} from '@/lib/labels'

export const Route = createFileRoute('/_app/')({
  component: DashboardPage,
})

const KPI_GRID = 'grid gap-4 md:grid-cols-2 xl:grid-cols-4'
const LINKED_CARD = 'hover:border-primary/40 h-full transition-colors'

function DashboardPage() {
  const overview = useAdminOverview()

  const layout = {
    icon: GaugeIcon,
    title: 'Dashboard',
    description: overview.data
      ? `Atualizado em ${formatDate(overview.data.generatedAt)}`
      : 'Receita, clientes e o que precisa de ação',
  }

  if (overview.isError) {
    return (
      <PageLayout {...layout}>
        <ErrorState onRetry={() => void overview.refetch()} />
      </PageLayout>
    )
  }

  if (!overview.data) {
    return (
      <PageLayout {...layout}>
        <div className={KPI_GRID}>
          {['MRR', 'Clientes pagantes', 'Recebido no mês', 'MRR em risco'].map(
            (label) => (
              <MetricCard key={label} label={label} value="" loading />
            )
          )}
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout {...layout}>
      <HealthAlerts data={overview.data} />
      <Kpis data={overview.data} />
      <div className="grid items-start gap-6 xl:grid-cols-[1.4fr_0.6fr]">
        <MrrTrend data={overview.data} />
        <MrrComposition data={overview.data} />
      </div>
      <div className="grid items-start gap-6 xl:grid-cols-[1.4fr_0.6fr]">
        <NeedsAction data={overview.data} />
        <SectionCard title="Eventos recentes" description="Cobrança e suporte">
          {overview.data.recentAudit.length === 0 ? (
            <EmptyState
              variant="inline"
              icon={ClipboardListIcon}
              title="Nenhum evento ainda"
            />
          ) : (
            <ol className="space-y-4">
              {overview.data.recentAudit.slice(0, 8).map((event) => (
                <AuditItem key={event.id} event={event} />
              ))}
            </ol>
          )}
        </SectionCard>
      </div>
    </PageLayout>
  )
}

/** Problemas da plataforma, não de um cliente: aparecem antes dos números. */
function HealthAlerts({ data }: { data: AdminOverview }) {
  const failures = data.summary.stripeWebhookFailures
  return (
    <>
      {failures.last7Days > 0 ? (
        <Alert variant="destructive">
          <ShieldAlertIcon />
          <AlertTitle>
            {formatNumber(failures.last7Days)}{' '}
            {failures.last7Days === 1
              ? 'webhook da Stripe falhou'
              : 'webhooks da Stripe falharam'}{' '}
            nos últimos 7 dias
          </AlertTitle>
          <AlertDescription>
            Status e faturas dessas assinaturas podem estar desatualizados. Veja
            o erro em Developers → Webhooks na Stripe e reenvie o evento.
          </AlertDescription>
        </Alert>
      ) : null}
      {data.truncated ? (
        <Alert>
          <AlertTriangleIcon />
          <AlertTitle>
            Mostrando {formatNumber(data.organizations.length)} de{' '}
            {formatNumber(data.summary.organizations)} organizações
          </AlertTitle>
          <AlertDescription>
            Os totais consideram todas; as listas, só as mais recentes.
          </AlertDescription>
        </Alert>
      ) : null}
    </>
  )
}

function Kpis({ data }: { data: AdminOverview }) {
  const { summary, mrrHistory } = data
  const previous = mrrHistory.previous
  const estimated = summary.mrrBySource.CATALOG
  const atRisk = summary.pastDue.mrrCents

  return (
    <div className={KPI_GRID}>
      <Link to="/receita" className="block">
        <MetricCard
          className={LINKED_CARD}
          label="MRR"
          value={formatCentsBRL(summary.mrrCents)}
          icon={LineChartIcon}
          trend={
            previous ? (
              <TrendBadge
                value={relativeChange(summary.mrrCents, previous.mrrCents)}
                comparedTo={`Comparado a ${formatDayMonth(previous.date, true)}`}
              />
            ) : null
          }
          chart={
            <Sparkline
              values={mrrHistory.series.map((point) => point.mrrCents)}
            />
          }
          hint={
            estimated > 0
              ? `${formatCentsBRL(estimated)} estimado pelo preço de tabela`
              : 'Stripe e contratos, sem estimativa'
          }
        />
      </Link>
      <Link to="/organizacoes" search={{ status: 'paying' }} className="block">
        <MetricCard
          className={LINKED_CARD}
          label="Clientes pagantes"
          value={formatNumber(summary.payingOrganizations)}
          icon={UsersIcon}
          hint={`Ticket médio ${formatCentsBRL(summary.arpaCents)} · ${formatNumber(summary.trialingOrganizations)} em teste`}
        />
      </Link>
      <Link to="/pagamentos" className="block">
        <MetricCard
          className={LINKED_CARD}
          label="Recebido no mês"
          value={formatCentsBRL(summary.receivedThisMonthCents)}
          icon={BanknoteIcon}
          tone={summary.failedPaymentsThisMonth > 0 ? 'warning' : 'default'}
          hint={`${formatCount(summary.invoicesPaidThisMonth, 'fatura paga', 'faturas pagas')} · ${formatNumber(summary.failedPaymentsThisMonth)} com falha`}
        />
      </Link>
      <Link
        to="/organizacoes"
        search={{ status: 'PAST_DUE' }}
        className="block"
      >
        <MetricCard
          className={LINKED_CARD}
          label="MRR em risco"
          value={formatCentsBRL(atRisk)}
          icon={AlertTriangleIcon}
          tone={atRisk > 0 ? 'destructive' : 'default'}
          hint={`${formatNumber(summary.pastDue.organizations)} em atraso · ${formatCount(summary.suspendedOrganizations, 'suspensa', 'suspensas')}`}
        />
      </Link>
    </div>
  )
}

function MrrTrend({ data }: { data: AdminOverview }) {
  const sync = useSyncRevenue()
  const series = data.mrrHistory.series
  return (
    <SectionCard
      title="MRR nos últimos 30 dias"
      description="Uma foto por dia, às 06:00 (horário de Brasília)."
      actions={
        <Button variant="outline" size="sm" asChild>
          <Link to="/receita">Ver receita</Link>
        </Button>
      }
    >
      {series.length >= 2 ? (
        <TimeSeriesChart
          data={series}
          series={[{ key: 'mrrCents', label: 'MRR' }]}
          formatValue={formatCentsBRL}
          height={220}
        />
      ) : (
        <EmptyState
          variant="inline"
          icon={LineChartIcon}
          title="O histórico começa a contar agora"
          description="O gráfico aparece a partir da segunda foto diária. Se ainda não há nenhuma, registre a primeira."
          action={
            series.length === 0 ? (
              <Button
                size="sm"
                disabled={sync.isPending}
                onClick={() => sync.mutate()}
              >
                Registrar MRR de hoje
              </Button>
            ) : undefined
          }
        />
      )}
    </SectionCard>
  )
}

function MrrComposition({ data }: { data: AdminOverview }) {
  const { summary } = data
  const sources = (['STRIPE', 'CONTRACT', 'CATALOG'] as const).filter(
    (source) => summary.mrrBySource[source] > 0
  )
  const facts: Array<{ label: string; value: ReactNode }> = [
    {
      label: 'Mudanças de plano agendadas',
      value:
        summary.scheduledDowngrades.organizations > 0
          ? `${formatCentsBRL(summary.scheduledDowngrades.mrrDeltaCents)} (${formatNumber(summary.scheduledDowngrades.organizations)})`
          : '—',
    },
    {
      label: 'Testes acabando em 7 dias',
      value: formatNumber(summary.trialsEndingSoon),
    },
    {
      label: 'Faturas em aberto',
      value:
        summary.openInvoices.count > 0
          ? `${formatCentsBRL(summary.openInvoices.cents)} (${formatNumber(summary.openInvoices.count)})`
          : '—',
    },
    {
      label: 'Cortesias',
      value: formatNumber(summary.courtesyOrganizations),
    },
  ]

  return (
    <SectionCard
      title="De onde vem o MRR"
      description="O que é exato e o que é estimativa."
    >
      <div className="space-y-5">
        {sources.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Nenhuma organização pagante.
          </p>
        ) : (
          <BarList
            max={summary.mrrCents}
            items={sources.map((source) => ({
              key: source,
              label: MRR_SOURCE_LABELS[source],
              value: summary.mrrBySource[source],
              display: formatCentsBRL(summary.mrrBySource[source]),
              hint: MRR_SOURCE_HINTS[source],
              color:
                source === 'CATALOG' ? 'var(--muted-foreground)' : undefined,
            }))}
          />
        )}
        <dl className="space-y-2 border-t pt-4 text-sm">
          {facts.map((fact) => (
            <div key={fact.label} className="flex justify-between gap-3">
              <dt className="text-muted-foreground">{fact.label}</dt>
              <dd className="font-medium tabular-nums">{fact.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </SectionCard>
  )
}

function NeedsAction({ data }: { data: AdminOverview }) {
  const navigate = useNavigate()
  const usdToBrl = data.exchangeRate.rate
  const rows = data.organizations
    .map((row) => ({ row, signals: attentionSignals(row, { usdToBrl }) }))
    .filter((item) => item.signals.length > 0)
    .sort(
      (a, b) =>
        (b.signals[0]?.priority ?? 0) - (a.signals[0]?.priority ?? 0) ||
        b.row.mrr.cents - a.row.mrr.cents
    )

  return (
    <SectionCard
      title="Precisa de ação"
      description="Cobrança, contrato, uso e oportunidades, do mais urgente ao oportuno."
      contentClassName="p-0"
      actions={
        rows.length > 0 ? (
          <Button variant="outline" size="sm" asChild>
            <Link to="/organizacoes" search={{ sinal: 'any' }}>
              Ver todas ({formatNumber(rows.length)})
            </Link>
          </Button>
        ) : null
      }
    >
      <DataTable
        variant="flush"
        rows={rows}
        pageSize={8}
        getRowKey={(item) => item.row.id}
        empty="Nada pendente: nenhuma organização com sinal de atenção."
        onRowClick={(item) =>
          void navigate({
            to: '/organizacoes/$orgId',
            params: { orgId: item.row.id },
          })
        }
        columns={[
          {
            key: 'org',
            header: 'Organização',
            render: ({ row }) => (
              <OrgCell
                id={row.id}
                name={row.name}
                slug={row.slug}
                logo={row.logo}
              />
            ),
          },
          {
            key: 'signal',
            header: 'Sinal',
            render: ({ signals }) => <AttentionBadges signals={signals} />,
          },
          {
            key: 'mrr',
            header: 'MRR',
            align: 'right',
            render: ({ row }) =>
              row.mrr.cents > 0 ? formatCentsBRL(row.mrr.cents) : '—',
          },
        ]}
      />
    </SectionCard>
  )
}

function AuditItem({ event }: { event: AdminAuditEvent }) {
  return (
    <li className="flex items-start gap-3">
      <span className="bg-primary mt-1.5 size-2 shrink-0 rounded-full" />
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">
          {translateAuditType(event.type)}
        </p>
        <p className="text-muted-foreground truncate text-xs">
          <Link
            to="/organizacoes/$orgId"
            params={{ orgId: event.organizationId }}
            className="hover:underline"
          >
            {event.organizationName}
          </Link>{' '}
          · {formatDate(event.createdAt)}
        </p>
      </div>
    </li>
  )
}
